/**
 * Edit Chart Tool — Factory (v2, with image + write tracking)
 *
 * Merges MAIN's factory/DI pattern with HEAD's v2 execution logic:
 * - XML-first chart editing with readId lineage (no etag)
 * - CDATA normalization for recovery
 * - Image tracking: returns _rawImages for host storage
 * - Write tracking: returns _excelReadInfos for host reminder system
 */
import { ToolBase, ToolDescription } from "../../../../../agents/base";
import { buildWriteEntry, collectImagesFromResult, } from "../../../../writeTracking";
import { EditChartParamsSchema, } from "./editChart.schema";
import { EXCEL_CHART_XML_REFERENCE } from "./excelChartXml.reference";
const SYSTEM_PROMPT_DESCRIPTION = `### editChart
Create, edit, or delete workbook charts using the XML-first contract (ExcelChartML v2).

**Actions:**
- \`create\` — provide full \`chartXml\` (real XML, NOT JSON)
- \`edit\` — provide \`changes[]\` (search/replace pairs) with optional \`readId\` lineage
- \`delete\` — remove chart by \`chartId\` or \`chartName\`
- \`createWaterfall\` — create a waterfall chart from category/value ranges

**Key rules:**
- No standalone move action; placement changes happen via XML edits on \`<placement>\`
- Always read chart first with \`readExcel.readCharts\` before editing
- Include \`readId\` from the prior read for concurrency-safe edit cycles
- \`chartXml\` must be real XML only — never JSON, never CDATA-wrapped

${EXCEL_CHART_XML_REFERENCE}`;
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function unwrapRequest(raw) {
    const obj = asObject(raw);
    const nested = obj.request;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }
    return obj;
}
function resolveSessionId(raw, fallback) {
    const obj = asObject(raw);
    return (typeof obj.sessionId === "string" ? obj.sessionId : undefined) || fallback;
}
function normalizeChartXmlPayload(chartXml) {
    if (!chartXml)
        return undefined;
    let xml = chartXml.trim();
    if (xml.startsWith("<![CDATA[") && xml.endsWith("]]>")) {
        xml = xml.slice(9, -3).trim();
        console.warn("[editChart] Stripped outer CDATA wrapper from chartXml (recovery path).");
    }
    return xml;
}
function normalizeChartRangeForInfo(chartRef) {
    if (!chartRef)
        return "CHART:unknown";
    return `CHART:${chartRef}`;
}
// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------
export function createEditChartTool(runner, fallbackSessionId) {
    return new EditChartTool(runner, fallbackSessionId).toRuntimeToolDefinition();
}
export class EditChartTool extends ToolBase {
    constructor(runner, fallbackSessionId) {
        super();
        this.runner = runner;
        this.fallbackSessionId = fallbackSessionId;
        this.name = "editChart";
        this.fieldsToCheck = [
            { name: "uiDescription", type: "string", list_status: false, order: 0 },
        ];
        this.description = new ToolDescription("editChart", "Create, edit, or delete workbook charts via XML-first contract (v2).", EditChartParamsSchema);
        this.paramsSchema = EditChartParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
    }
    setSessionId(sessionId) {
        this.fallbackSessionId = sessionId;
    }
    async execute(args) {
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        const params = unwrapRequest(args);
        const normalizedChartXml = normalizeChartXmlPayload(params.chartXml);
        const request = {
            sheetName: params.sheetName,
            action: params.action,
            chartId: params.chartId,
            chartName: params.chartName,
            chartXml: normalizedChartXml,
            changes: params.changes,
            readId: params.readId,
            categoryRange: params.categoryRange,
            valuesRange: params.valuesRange,
            totalPoints: params.totalPoints,
            waterfallStyle: params.waterfallStyle,
            title: params.title,
            numberFormat: params.numberFormat,
            placement: params.placement,
            helperDataRange: params.helperDataRange,
            showConnectorLines: params.showConnectorLines,
            showDataLabels: params.showDataLabels,
            gapWidth: params.gapWidth,
        };
        console.log(`[agent-runtime/editChart] action=${params.action} sheet=${params.sheetName} ` +
            `chartId=${params.chartId || "(none)"} readId=${params.readId || "(none)"} ` +
            `changes=${params.changes?.length ?? 0}`);
        const runnerResult = await this.runner.editChart(request, { sessionId });
        const data = asObject(runnerResult.data);
        const chartRef = data.chartId || params.chartId || params.chartName;
        const chartLabel = (chartRef || "chart").replace(/[^a-zA-Z0-9]/g, "_");
        const imageIds = [];
        const rawImages = [];
        const chartImageBase64 = data.chartImageBase64;
        if (chartImageBase64) {
            const collected = collectImagesFromResult([{ base64: chartImageBase64, label: chartLabel }], "chart");
            imageIds.push(...collected.imageIds);
            rawImages.push(...collected.rawImages);
        }
        const excelReadInfos = [];
        const detailLevel = params.action === "delete" ? "writeChartDelete" : "writeChart";
        excelReadInfos.push(buildWriteEntry("content", params.sheetName, normalizeChartRangeForInfo(chartRef), detailLevel, imageIds[0]));
        const extensions = {
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _rawImages: rawImages.length > 0 ? rawImages : undefined,
            _excelReadInfos: excelReadInfos.length > 0 ? excelReadInfos : undefined,
        };
        return {
            success: runnerResult.success,
            action: params.action,
            chartId: data.chartId || params.chartId || "",
            chartName: data.chartName || params.chartName,
            message: data.message,
            warnings: runnerResult.warnings,
            error: data.error || undefined,
            errors: runnerResult.errors,
            excelDataSource: data.excelDataSource,
            updatedXml: data.updatedXml || undefined,
            chartXml: data.chartXml || undefined,
            readId: data.readId || undefined,
            dataMode: data.dataMode,
            chartImageBase64: undefined,
            operations: data.operations,
            details: data.details,
            ...extensions,
        };
    }
}
